import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { apiViewer, formatApiError } from "@/lib/api";

const INPUT_CLS =
  "w-full rounded-xl border border-stone-700/70 bg-ink-900/80 px-4 py-3 text-sm text-stone-100 placeholder:text-stone-500 transition-colors focus:border-gold-500/60 focus:outline-none focus:ring-1 focus:ring-gold-500/40";
const LABEL_CLS = "mb-2 block text-[10px] uppercase tracking-[0.3em] text-stone-400";
const BTN_CLS =
  "inline-flex w-full items-center justify-center gap-3 rounded-full bg-gradient-to-r from-gold-600 via-gold-400 to-gold-600 px-8 py-3.5 text-[12px] font-bold uppercase tracking-[0.2em] text-ink-950 transition-all duration-300 hover:shadow-[0_0_28px_rgba(212,175,55,0.4)] disabled:opacity-60";

export default function OtpResetForm({ initialIdentifier = "", onDone, onBack }) {
  const [step, setStep] = useState(1);
  const [identifier, setIdentifier] = useState(initialIdentifier);
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [demoOtp, setDemoOtp] = useState(null);
  const [busy, setBusy] = useState(false);

  const sendOtp = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await apiViewer.post("/auth/forgot-password", { identifier });
      setDemoOtp(data.delivered ? null : data.demo_otp);
      toast.success(data.message);
      setStep(2);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await apiViewer.post("/auth/verify-otp", { identifier, otp });
      setResetToken(data.reset_token);
      setStep(3);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await apiViewer.post("/auth/reset-password", {
        identifier,
        reset_token: resetToken,
        new_password: password,
      });
      onDone(data.message || "Password updated successfully! Please Sign In.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <button
        type="button"
        data-testid="otp-back-button"
        onClick={onBack}
        className="self-start text-[11px] font-semibold uppercase tracking-[0.2em] text-stone-400 transition-colors hover:text-gold-300"
      >
        ← Back to Sign In
      </button>

      {step === 1 && (
        <form onSubmit={sendOtp} data-testid="otp-form-identifier" className="space-y-4">
          <div>
            <label htmlFor="otp-identifier" className={LABEL_CLS}>Registered Email or Mobile Number</label>
            <input
              id="otp-identifier"
              data-testid="otp-identifier-input"
              required
              placeholder="you@example.com or 9876543210"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              className={INPUT_CLS}
            />
          </div>
          <button type="submit" data-testid="otp-send-button" disabled={busy} className={BTN_CLS}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Send OTP
          </button>
        </form>
      )}

      {step === 2 && (
        <form onSubmit={verifyOtp} data-testid="otp-form-verify" className="space-y-4">
          {demoOtp && (
            <p data-testid="otp-demo-note" className="rounded-xl border border-gold-500/20 bg-gold-500/5 px-4 py-3 text-center text-[11px] text-gold-300">
              Demo mode — simulated 6-digit OTP. Test code: {demoOtp}
            </p>
          )}
          <div>
            <label htmlFor="otp-code" className={LABEL_CLS}>Enter 6-Digit OTP</label>
            <input
              id="otp-code"
              data-testid="otp-input"
              required
              inputMode="numeric"
              maxLength={6}
              placeholder="••••••"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              className={`${INPUT_CLS} text-center text-2xl font-semibold tracking-[0.5em]`}
            />
          </div>
          <button type="submit" data-testid="otp-verify-button" disabled={busy || otp.length !== 6} className={BTN_CLS}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Verify OTP
          </button>
        </form>
      )}

      {step === 3 && (
        <form onSubmit={resetPassword} data-testid="otp-form-reset" className="space-y-4">
          <div>
            <label htmlFor="otp-new-password" className={LABEL_CLS}>New Password</label>
            <input
              id="otp-new-password"
              data-testid="otp-new-password-input"
              type="password"
              required
              minLength={6}
              placeholder="Minimum 6 characters"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={INPUT_CLS}
            />
          </div>
          <div>
            <label htmlFor="otp-confirm-password" className={LABEL_CLS}>Confirm New Password</label>
            <input
              id="otp-confirm-password"
              data-testid="otp-confirm-password-input"
              type="password"
              required
              minLength={6}
              placeholder="Re-enter new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className={INPUT_CLS}
            />
          </div>
          <button type="submit" data-testid="otp-update-button" disabled={busy} className={BTN_CLS}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Update Password
          </button>
        </form>
      )}
    </div>
  );
}
